import React from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  Card,
} from '@mui/material';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactFormDialog from '../components/ContactFormDialog';

const TermsOfServicePage: React.FC = () => {
  const [contactOpen, setContactOpen] = React.useState(false);
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Navbar />

      <Box sx={{ pt: 15, pb: 10 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip label="Legal" color="secondary" sx={{ mb: 2 }} />
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              Terms of Service
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Last updated: February 14, 2026
            </Typography>
          </Box>

          <Card sx={{ p: 4, mb: 4 }}>
            <Typography variant="body1" paragraph>
              Welcome to CleoLead. By accessing or using the services provided by CleoLead Corp. ("CleoLead", "we", "our", or "us"),
              you agree to be bound by these Terms of Service ("Terms"). Please read them carefully before using our platform.
            </Typography>
          </Card>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            1. Acceptance of Terms
          </Typography>
          <Typography variant="body1" paragraph>
            By creating an account or using CleoLead services, you agree to these Terms and our Privacy Policy.
            If you do not agree to these Terms, you may not access or use our services.
          </Typography>
          <Typography variant="body1" paragraph>
            We may modify these Terms at any time. We will notify you of material changes via email or through our
            platform. Your continued use of our services after changes constitutes acceptance of the new Terms.
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            2. Description of Service
          </Typography>
          <Typography variant="body1" paragraph>
            CleoLead is an AI Business Manager platform that helps businesses sell, support,
            and organize across phone, chat, SMS, email, and social channels (including Facebook
            Messenger, Instagram, WhatsApp, and Telegram). Our services include:
          </Typography>
          <Typography component="ul" sx={{ pl: 3 }}>
            <li>AI receptionist for inbound and outbound phone calls and SMS</li>
            <li>AI helpdesk and sales agent across messaging and web chat</li>
            <li>AI chat trained on the customer's business documents, website, and past conversations</li>
            <li>Calendar, reservation, and meeting management</li>
            <li>Lead capture, scoring, and CRM data export</li>
            <li>Multi-channel broadcast and re-engagement</li>
            <li>Analytics and reporting</li>
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            3. Account Registration
          </Typography>
          <Typography variant="body1" paragraph>
            To use our services, you must create an account. You agree to:
          </Typography>
          <Typography component="ul" sx={{ pl: 3 }}>
            <li>Provide accurate and complete information</li>
            <li>Maintain the security of your account credentials</li>
            <li>Notify us immediately of any unauthorized access</li>
            <li>Be responsible for all activities under your account</li>
          </Typography>
          <Typography variant="body1" paragraph sx={{ mt: 2 }}>
            You must be at least 18 years old and have the legal authority to bind your organization to these Terms.
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            4. Acceptable Use
          </Typography>
          <Typography variant="body1" paragraph>
            You agree to use our services only for lawful purposes. You may not:
          </Typography>
          <Typography component="ul" sx={{ pl: 3 }}>
            <li>Violate any applicable laws or regulations</li>
            <li>Send spam, unsolicited messages, or misleading content</li>
            <li>Impersonate others or provide false information</li>
            <li>Interfere with or disrupt our services</li>
            <li>Attempt to gain unauthorized access to our systems</li>
            <li>Use our services to harass, abuse, or harm others</li>
            <li>Violate the terms of connected messaging platforms</li>
            <li>Use our AI to generate illegal, harmful, or deceptive content</li>
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            4a. AI Transparency Requirements
          </Typography>
          <Typography variant="body1" paragraph>
            When using CleoLead's AI features on messaging platforms, you agree to:
          </Typography>
          <Typography component="ul" sx={{ pl: 3 }}>
            <li>Inform your end-customers that automated AI responses may be used</li>
            <li>Provide a clear way for customers to request human assistance</li>
            <li>Not misrepresent AI-generated messages as coming from a human</li>
            <li>Comply with all applicable AI disclosure laws in your jurisdiction</li>
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            4b. Platform Compliance
          </Typography>
          <Typography variant="body1" paragraph>
            When connecting Facebook, Instagram, or other messaging platforms:
          </Typography>
          <Typography component="ul" sx={{ pl: 3 }}>
            <li>You must comply with Meta Platform Terms and Messaging Policies</li>
            <li>You must respect the 24-hour messaging window for automated responses</li>
            <li>You must not use the service for unsolicited mass messaging or spam</li>
            <li>You must not use the service for cold outreach to users who have not initiated contact</li>
            <li>Violation of platform terms may result in suspension of your account</li>
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            5. Subscription and Payment
          </Typography>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
            5.1 Pricing
          </Typography>
          <Typography variant="body1" paragraph>
            Our services are offered under various subscription plans. Prices are listed on our website and may
            change with notice. You agree to pay all fees associated with your selected plan.
          </Typography>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
            5.2 Billing
          </Typography>
          <Typography variant="body1" paragraph>
            Subscriptions are billed in advance on a monthly or annual basis. Payment is due at the start of each
            billing period. We use Stripe for payment processing.
          </Typography>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
            5.3 Cancellation
          </Typography>
          <Typography variant="body1" paragraph>
            You may cancel your subscription at any time. Cancellations take effect at the end of the current
            billing period. We do not provide refunds for partial billing periods.
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            6. Intellectual Property
          </Typography>
          <Typography variant="body1" paragraph>
            CleoLead and its licensors retain all rights to our services, including software, designs,
            trademarks, and documentation. You retain ownership of your content and data.
          </Typography>
          <Typography variant="body1" paragraph>
            You grant us a license to use your content solely to provide and improve our services. This license
            ends when you delete your content or close your account.
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            7. Data and Privacy
          </Typography>
          <Typography variant="body1" paragraph>
            Our collection and use of personal information is governed by our Privacy Policy. You are responsible
            for ensuring your use of our services complies with applicable data protection laws, including obtaining
            necessary consents from your customers.
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            8. Disclaimer of Warranties
          </Typography>
          <Typography variant="body1" paragraph>
            OUR SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT
            WARRANT THAT OUR SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE. WE DISCLAIM ALL WARRANTIES
            INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            9. Limitation of Liability
          </Typography>
          <Typography variant="body1" paragraph>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, CLEOLEAD SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
            SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED
            DIRECTLY OR INDIRECTLY. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE PAST
            TWELVE MONTHS.
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            10. Indemnification
          </Typography>
          <Typography variant="body1" paragraph>
            You agree to indemnify and hold harmless CleoLead and its affiliates from any claims, damages,
            or expenses arising from your use of our services, your content, or your violation of these Terms.
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            11. Termination
          </Typography>
          <Typography variant="body1" paragraph>
            We may suspend or terminate your account if you violate these Terms or for any other reason with notice.
            Upon termination, your right to use our services ends immediately. We will retain your data for a
            reasonable period to allow you to export it.
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            12. Governing Law
          </Typography>
          <Typography variant="body1" paragraph>
            These Terms are governed by the laws of the Province of Alberta, Canada, without regard to conflict of law
            principles. Any disputes shall be resolved in the courts of the Province of Alberta, Canada.
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            13. Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have questions about these Terms, please contact us at:
          </Typography>
          <Card sx={{ p: 3, bgcolor: 'background.paper' }}>
            <Typography variant="body1">
              <strong>Email:</strong> <span onClick={() => setContactOpen(true)} style={{ color: '#C75B39', cursor: 'pointer', textDecoration: 'underline' }} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') setContactOpen(true); }}>support@cleolead.com</span>
            </Typography>
          </Card>
        </Container>
      </Box>

      <Footer />
      <ContactFormDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        title="Terms of Service Inquiry"
        subtitle="Have questions about our Terms of Service? Send us a message."
        formType="support"
        defaultSubject="Terms of Service Inquiry"
      />
    </Box>
  );
};

export default TermsOfServicePage;
